// src/components/UploadPersonnelDialog.js
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { CloudUpload as UploadIcon } from '@mui/icons-material';
import { validateCSVData, bulkUploadPersonnel } from '../services/personnelService';

/**
 * Proper CSV parser (RFC 4180).
 * Handles quoted fields, commas inside quotes, escaped quotes (""),
 * CRLF or LF line endings, and a leading UTF-8 BOM.
 * The previous line.split(',') version corrupted every quoted file.
 */
const parseCSVText = (text) => {
  const clean = text.replace(/^\uFEFF/, ''); // strip UTF-8 BOM
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < clean.length; i++) {
    const ch = clean[i];

    if (inQuotes) {
      if (ch === '"') {
        if (clean[i + 1] === '"') {
          field += '"'; // escaped quote
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field);
      field = '';
    } else if (ch === '\r') {
      // ignore; the \n that follows ends the row
    } else if (ch === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += ch;
    }
  }
  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => String(c).trim() !== ''));
};

const UploadPersonnelDialog = ({ open, commands, onClose, onSuccess }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [commandId, setCommandId] = useState('');
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [validationResult, setValidationResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const steps = ['Select Command', 'Upload CSV', 'Validate', 'Confirm Upload'];

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      parseCSV(selectedFile);
    }
  };

  const parseCSV = (selectedFile) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const rows = parseCSVText(e.target.result);

      if (rows.length < 2) {
        setError('The CSV has no data rows below the header.');
        setActiveStep(1);
        return;
      }

      const headers = rows[0].map((h) => String(h).trim());
      const data = [];
      for (let i = 1; i < rows.length; i++) {
        const values = rows[i];
        const record = {};
        headers.forEach((header, index) => {
          const v = values[index];
          record[header] = v === undefined || v === null ? '' : String(v).trim();
        });
        data.push(record);
      }

      setCsvData(data);
      setActiveStep(2);
      // Auto-validate as soon as the file is parsed (no manual Validate click)
      handleValidate(data);
    };
    reader.readAsText(selectedFile);
  };

  const handleValidate = async (records) => {
    const payload = Array.isArray(records) ? records : csvData;
    setLoading(true);
    setError('');
    try {
      const response = await validateCSVData(payload, commandId);
      setValidationResult(response.data);
      setActiveStep(3);
    } catch (err) {
      setError(err.response?.data?.message || 'Validation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!validationResult || validationResult.valid === 0) {
      setError('No valid records to upload');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await bulkUploadPersonnel(validationResult.validRecords, commandId);
      handleClose();
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setActiveStep(0);
      setCommandId('');
      setFile(null);
      setCsvData([]);
      setValidationResult(null);
      setError('');
      onClose();
    }
  };

  const handleNext = () => {
    if (activeStep === 0 && !commandId) {
      setError('Please select a command');
      return;
    }
    if (activeStep === 1 && !file) {
      setError('Please select a CSV file');
      return;
    }
    if (activeStep === 2) {
      // Validation runs automatically after file parse; nothing to do here.
      return;
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError('');
  };

  const personName = (rec) =>
    `${rec?.lastName || ''} ${rec?.firstName || ''}`.trim() || '-';

  // Download the rejected rows so they can be fixed at source
  const downloadProblemRows = () => {
    const lines = [['Row', 'Type', 'Service No.', 'IPPIS No.', 'Name', 'Reason']];
    (validationResult.errors || []).forEach((e) => {
      lines.push([
        e.row,
        'Invalid',
        e.record?.serviceNumber || '',
        e.record?.ippisNumber || '',
        personName(e.record),
        (e.errors || []).join('; '),
      ]);
    });
    (validationResult.duplicateRecords || []).forEach((d) => {
      lines.push([
        d.row,
        'Duplicate',
        d.record?.serviceNumber || '',
        d.record?.ippisNumber || '',
        personName(d.record),
        d.reason || '',
      ]);
    });
    const csv = lines
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rejected_rows_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const problemTable = (items, kind) => (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Row</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Service No.</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>IPPIS No.</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item, idx) => (
            <TableRow key={`${kind}-${item.row}-${idx}`} hover>
              <TableCell>{item.row}</TableCell>
              <TableCell>{item.record?.serviceNumber || '-'}</TableCell>
              <TableCell>{item.record?.ippisNumber || '-'}</TableCell>
              <TableCell>{personName(item.record)}</TableCell>
              <TableCell>
                {kind === 'invalid' ? (
                  (item.errors || []).map((msg, i) => (
                    <Chip
                      key={i}
                      label={msg}
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))
                ) : (
                  <Typography variant="body2">{item.reason}</Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Upload Personnel CSV</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {activeStep === 0 && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Select the command for this personnel upload:
              </Typography>
              <TextField
                fullWidth
                select
                label="Command"
                value={commandId}
                onChange={(e) => setCommandId(e.target.value)}
                margin="normal"
              >
                {commands.map((command) => (
                  <MenuItem key={command.id} value={command.id}>
                    {command.code} - {command.name} ({command.zone})
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Upload personnel CSV file:
              </Typography>
              <Paper
                variant="outlined"
                sx={{ p: 3, textAlign: 'center', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
              >
                <input type="file" accept=".csv" onChange={handleFileChange} style={{ display: 'none' }} id="csv-upload" />
                <label htmlFor="csv-upload">
                  <Button variant="contained" component="span" startIcon={<UploadIcon />}>
                    Choose CSV File
                  </Button>
                </label>
                {file && (
                  <Typography variant="body2" sx={{ mt: 2 }}>Selected: {file.name}</Typography>
                )}
              </Paper>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>CSV columns (header row, in any order):</strong>
                  <br />
                  serviceNumber, ippisNumber, firstName, middleName, lastName,
                  gender, birthDate, rankName, gradeName, gradeLevel, gradeStep,
                  dateOfFirstAppointment, expectedRetirementDate, bankName,
                  accountNumber, pfaName, pencomId, nhfNumber
                  <br /><br />
                  Required: serviceNumber, ippisNumber, firstName, lastName,
                  gender, rankName, gradeName, gradeLevel, gradeStep, bankName,
                  accountNumber, pfaName, pencomId. middleName, birthDate,
                  dateOfFirstAppointment, expectedRetirementDate and nhfNumber
                  are optional. Dates as YYYY-MM-DD. Gender as MALE or FEMALE.
                  <br /><br />
                  Quoted fields are supported, so names or addresses containing
                  commas are read correctly.
                </Typography>
              </Alert>
            </Box>
          )}

          {activeStep === 2 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
              <Typography variant="body1" sx={{ mt: 2 }}>Validating CSV data...</Typography>
            </Box>
          )}

          {activeStep === 3 && validationResult && (
            <Box>
              <Typography variant="h6" gutterBottom>Validation Results</Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">{validationResult.total}</Typography>
                    <Typography variant="body2">Total Records</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">{validationResult.valid}</Typography>
                    <Typography variant="body2">Valid</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="error.main">{validationResult.invalid}</Typography>
                    <Typography variant="body2">Invalid</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main">{validationResult.duplicates}</Typography>
                    <Typography variant="body2">Duplicates</Typography>
                  </Box>
                </Box>
              </Paper>

              {(validationResult.invalid > 0 || validationResult.duplicates > 0) && (
                <Box sx={{ mb: 2, textAlign: 'right' }}>
                  <Button size="small" variant="outlined" onClick={downloadProblemRows}>
                    Download rejected rows
                  </Button>
                </Box>
              )}

              {validationResult.invalid > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {validationResult.invalid} record(s) rejected and will be skipped.
                    The row number is the line in your CSV file.
                  </Alert>
                  {problemTable(validationResult.errors || [], 'invalid')}
                </Box>
              )}

              {validationResult.duplicates > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Alert severity="info" sx={{ mb: 1 }}>
                    {validationResult.duplicates} record(s) are already in this exercise and will be skipped.
                  </Alert>
                  {problemTable(validationResult.duplicateRecords || [], 'duplicate')}
                </Box>
              )}

              {validationResult.valid > 0 ? (
                <Alert severity="success">{validationResult.valid} valid records ready to upload.</Alert>
              ) : (
                <Alert severity="error">No valid records to upload. Please fix errors in your CSV and try again.</Alert>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        {activeStep > 0 && activeStep < 3 && (
          <Button onClick={handleBack} disabled={loading}>Back</Button>
        )}
        {activeStep < 2 && (
          <Button onClick={handleNext} variant="contained" disabled={loading}>
            Next
          </Button>
        )}
        {activeStep === 3 && (
          <Button onClick={handleUpload} variant="contained" color="success" disabled={loading || !validationResult || validationResult.valid === 0}>
            {loading ? <CircularProgress size={24} /> : 'Upload Personnel'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UploadPersonnelDialog;